import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Package, Warning, Clock, Trash, MinusCircle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const STATUS_STYLES = {
  available: 'bg-green-100 text-green-700',
  reserved: 'bg-blue-100 text-blue-700',
  in_use: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-red-100 text-red-700',
  depleted: 'bg-gray-200 text-gray-600',
};

const MATERIAL_TYPES = ['raw_material', 'chemical', 'finished_goods'];

export default function FIFOPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'qa_manager' || user?.access_level === 'edit';
  const [inventory, setInventory] = useState([]);
  const [alerts, setAlerts] = useState({ expiring_soon: [], use_first: [], expired: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUseModal, setShowUseModal] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('inventory');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invRes, alertsRes] = await Promise.all([
        api.get('/fifo/inventory'),
        api.get('/fifo/alerts'),
      ]);
      setInventory(invRes.data);
      setAlerts(alertsRes.data);
    } catch (e) {
      toast.error('Failed to load FIFO data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      material_code: fd.get('material_code'),
      material_name: fd.get('material_name'),
      material_type: fd.get('material_type'),
      batch_number: fd.get('batch_number'),
      lot_number: fd.get('lot_number') || null,
      receipt_date: fd.get('receipt_date'),
      manufacturing_date: fd.get('manufacturing_date') || null,
      expiry_date: fd.get('expiry_date') || null,
      initial_quantity: parseFloat(fd.get('initial_quantity')),
      unit: fd.get('unit'),
      storage_location: fd.get('storage_location'),
      warehouse: fd.get('warehouse') || null,
      rack_number: fd.get('rack_number') || null,
      supplier_name: fd.get('supplier_name') || null,
      supplier_batch: fd.get('supplier_batch') || null,
      expiry_alert_days: parseInt(fd.get('expiry_alert_days') || '30', 10),
    };
    try {
      await api.post('/fifo/inventory', payload);
      toast.success('Inventory added');
      setShowCreateModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add inventory');
    }
  };

  const handleUse = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      inventory_id: showUseModal.id,
      quantity_used: parseFloat(fd.get('quantity_used')),
      used_for: fd.get('used_for') || null,
    };
    try {
      await api.post(`/fifo/inventory/${showUseModal.id}/use`, payload);
      toast.success('Usage recorded');
      setShowUseModal(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record usage');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await api.delete(`/fifo/inventory/${id}`);
      toast.success('Inventory item deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = inventory.filter(i => {
    if (filterType !== 'all' && i.material_type !== filterType) return false;
    if (filterStatus !== 'all' && i.status !== filterStatus) return false;
    return true;
  });

  const alertsCount = alerts.expired.length + alerts.expiring_soon.length + alerts.use_first.length;

  return (
    <div data-testid="fifo-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">FIFO Inventory</h1>
          <p className="mt-1 text-sm text-gray-600">First-In-First-Out material tracking with expiry & age alerts</p>
        </div>
        {canEdit && (
          <button
            data-testid="add-inventory-button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
          >
            <Plus size={20} weight="bold" />
            Add Inventory
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1">
          <button
            data-testid="tab-inventory"
            onClick={() => setActiveTab('inventory')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'inventory' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Inventory ({inventory.length})
          </button>
          <button
            data-testid="tab-alerts"
            onClick={() => setActiveTab('alerts')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'alerts' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Alerts
            {alertsCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">{alertsCount}</span>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading inventory...</div>
      ) : activeTab === 'inventory' ? (
        <>
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <select
              data-testid="filter-material-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm"
            >
              <option value="all">All Material Types</option>
              {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
            <select
              data-testid="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm"
            >
              <option value="all">All Statuses</option>
              {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
              <Package size={40} className="mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">No inventory found.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Material</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Batch</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Received</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Expiry</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Quantity</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Location</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                    {canEdit && <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, idx) => (
                    <tr key={item.id} data-testid={`fifo-row-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.material_name}</div>
                        <div className="text-xs text-gray-500">{item.material_code} • {item.material_type?.replace('_', ' ')}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{item.batch_number}</td>
                      <td className="px-4 py-3">{new Date(item.receipt_date).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {item.current_quantity} / {item.initial_quantity} {item.unit}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {item.storage_location}
                        {item.rack_number && <div className="text-gray-500">Rack {item.rack_number}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-sm ${STATUS_STYLES[item.status] || 'bg-gray-100 text-gray-700'}`}>
                          {item.status?.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {item.current_quantity > 0 && item.status === 'available' && (
                              <button
                                data-testid={`use-inventory-${idx}`}
                                onClick={() => setShowUseModal(item)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-sm"
                                title="Use inventory"
                              >
                                <MinusCircle size={16} />
                              </button>
                            )}
                            {(user?.role === 'admin' || user?.role === 'qa_manager') && (
                              <button
                                data-testid={`delete-inventory-${idx}`}
                                onClick={() => handleDelete(item.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"
                                title="Delete"
                              >
                                <Trash size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-6">
          {/* Expired */}
          <div>
            <h2 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
              <Warning size={20} weight="fill" /> Expired ({alerts.expired.length})
            </h2>
            {alerts.expired.length === 0 ? (
              <p className="text-sm text-gray-500">No expired materials.</p>
            ) : (
              <div className="space-y-2">
                {alerts.expired.map((item, idx) => (
                  <div key={item.id} data-testid={`expired-${idx}`} className="bg-red-50 border border-red-200 rounded-sm p-4">
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-medium text-gray-900">{item.material_name} <span className="text-xs font-mono text-gray-500">({item.batch_number})</span></p>
                        <p className="text-sm text-red-700">Expired {item.days_expired} days ago — {item.current_quantity} {item.unit} remaining</p>
                      </div>
                      <p className="text-xs text-gray-600">{item.storage_location}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Expiring Soon */}
          <div>
            <h2 className="text-lg font-semibold text-orange-700 mb-3 flex items-center gap-2">
              <Clock size={20} weight="fill" /> Expiring Soon ({alerts.expiring_soon.length})
            </h2>
            {alerts.expiring_soon.length === 0 ? (
              <p className="text-sm text-gray-500">No materials expiring soon.</p>
            ) : (
              <div className="space-y-2">
                {alerts.expiring_soon.map((item, idx) => (
                  <div key={item.id} data-testid={`expiring-${idx}`} className="bg-orange-50 border border-orange-200 rounded-sm p-4">
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-medium text-gray-900">{item.material_name} <span className="text-xs font-mono text-gray-500">({item.batch_number})</span></p>
                        <p className="text-sm text-orange-700">Expires in {item.days_until_expiry} days — {item.current_quantity} {item.unit} remaining</p>
                      </div>
                      <p className="text-xs text-gray-600">{item.storage_location}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Use First (FIFO) */}
          <div>
            <h2 className="text-lg font-semibold text-blue-700 mb-3 flex items-center gap-2">
              <Package size={20} weight="fill" /> Use Older Stock First ({alerts.use_first.length})
            </h2>
            {alerts.use_first.length === 0 ? (
              <p className="text-sm text-gray-500">No FIFO violations detected.</p>
            ) : (
              <div className="space-y-2">
                {alerts.use_first.map((item, idx) => (
                  <div key={`${item.material_code}-${idx}`} data-testid={`use-first-${idx}`} className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                    <p className="font-medium text-gray-900">{item.material_name} ({item.material_code})</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Use batch <span className="font-mono">{item.oldest_batch}</span> first (received {new Date(item.oldest_receipt_date).toLocaleDateString()}, {item.oldest_quantity} remaining). {item.newer_batches_count} newer batch(es) in stock.
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Add Inventory Batch</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded-sm">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">MATERIAL CODE *</label>
                  <input name="material_code" data-testid="mat-code" required className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">MATERIAL NAME *</label>
                  <input name="material_name" data-testid="mat-name" required className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">MATERIAL TYPE *</label>
                  <select name="material_type" data-testid="mat-type" required className="w-full px-3 py-2 border border-gray-300 rounded-sm">
                    {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">BATCH NUMBER *</label>
                  <input name="batch_number" data-testid="mat-batch" required className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">LOT NUMBER</label>
                  <input name="lot_number" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">UNIT *</label>
                  <select name="unit" required defaultValue="kg" className="w-full px-3 py-2 border border-gray-300 rounded-sm">
                    <option value="kg">kg</option>
                    <option value="liters">liters</option>
                    <option value="pieces">pieces</option>
                    <option value="meters">meters</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">RECEIPT DATE *</label>
                  <input name="receipt_date" data-testid="mat-receipt" type="date" required defaultValue={new Date().toISOString().slice(0, 10)} className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">MANUFACTURING DATE</label>
                  <input name="manufacturing_date" type="date" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">EXPIRY DATE</label>
                  <input name="expiry_date" data-testid="mat-expiry" type="date" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">EXPIRY ALERT DAYS</label>
                  <input name="expiry_alert_days" type="number" defaultValue="30" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">INITIAL QUANTITY *</label>
                  <input name="initial_quantity" data-testid="mat-qty" type="number" step="0.01" required className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">STORAGE LOCATION *</label>
                  <input name="storage_location" data-testid="mat-location" required className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">WAREHOUSE</label>
                  <input name="warehouse" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">RACK NUMBER</label>
                  <input name="rack_number" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">SUPPLIER NAME</label>
                  <input name="supplier_name" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">SUPPLIER BATCH</label>
                  <input name="supplier_batch" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" data-testid="mat-submit" className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800">
                  Add Inventory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Use Modal */}
      {showUseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Record Usage</h2>
              <button onClick={() => setShowUseModal(null)} className="p-1 hover:bg-gray-100 rounded-sm">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUse} className="p-6 space-y-4">
              <div className="p-3 bg-gray-50 rounded-sm text-sm">
                <p className="font-medium">{showUseModal.material_name}</p>
                <p className="text-gray-600 text-xs mt-1">Batch: {showUseModal.batch_number}</p>
                <p className="text-gray-600 text-xs">Available: {showUseModal.current_quantity} {showUseModal.unit}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">QUANTITY USED *</label>
                <input
                  name="quantity_used"
                  data-testid="use-quantity"
                  type="number"
                  step="0.01"
                  required
                  max={showUseModal.current_quantity}
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">USED FOR (Project / Reference)</label>
                <input name="used_for" data-testid="use-reason" className="w-full px-3 py-2 border border-gray-300 rounded-sm" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowUseModal(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" data-testid="use-submit" className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800">
                  Record Usage
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
